import { useCallback, useEffect, useState } from 'react';
import { PaymentBlockProps } from './PaymentBlock';
import { PaymentElement, useStripe, useElements, Elements } from '@stripe/react-stripe-js';
import Checkable from '../components/Checkable';
import { useTranslation } from 'react-i18next';
import { MINIMUM_PAYMENT_AMOUNT } from '../../config';
import { useLang } from '../hooks/useLanguage';
import { toStripeLocale } from '../helpers/stripeLocale';

// Match Stripe Payment Element to per-show theme. Reads inline style (not
// computed) so we only pick up values applied by useShowStyling, and we re-key
// Elements by the result so it re-mounts if the primary lands after first paint.
function stripeAppearance(): { theme: 'night' | 'stripe'; variables?: { colorPrimary: string } } {
    const root = typeof document !== 'undefined' && document.getElementById('fireknife-widget-root');
    if (!root) return { theme: 'stripe' };
    const colorPrimary = root.style.getPropertyValue('--fireknife-primary').trim();
    const themeAttr = root.getAttribute('data-fireknife-theme');
    const isDark =
        themeAttr === 'dark' ||
        (themeAttr === 'auto' && window.matchMedia('(prefers-color-scheme: dark)').matches);
    const theme = isDark ? 'night' : 'stripe';
    return colorPrimary ? { theme, variables: { colorPrimary } } : { theme };
}

function StripeInnerForm({
    onStartTransaction,
    methodInfo,
    onCancel,
    amount,
    maxAmount,
}: {
    onStartTransaction: (paymentInfo: Record<string, any>) => Promise<void>;
    methodInfo: Record<string, any>;
    onCancel?: () => void;
    amount?: number;
    maxAmount?: number;
}) {
    const { t } = useTranslation();
    const stripe = useStripe();
    const elements = useElements();
    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        if (!stripe || !elements) {
            return;
        }

        const { error, setupIntent } = await stripe.confirmSetup({
            elements,
            confirmParams: {
                return_url: window.location.href,
            },
            redirect: 'if_required',
        });

        if (error) {
            console.error('Error confirming payment:', error);
            return;
        }

        if (setupIntent) {
            console.log('Payment successful:', setupIntent);
            // TODO: continue to take this and create a transaction, finish up order and tick status
            onStartTransaction({
                customerId: methodInfo?.customerId,
                setupResult: setupIntent,
            });
        }
    };

    if (!stripe || !elements) {
        return;
    }
    return (
        <form className="fireknife-payment-methods__stripe-form" onSubmit={handleSubmit}>
            <PaymentElement options={{ layout: 'tabs' }} />
            <div className="fireknife-payment-methods__btn-box">
                {onCancel && (
                    <button
                        className="fireknife-payment-methods__btn fireknife-payment-methods__btn--cancel fireknife-btn fireknife-btn--secondary"
                        onClick={onCancel}
                        type="button"
                    >
                        {t('common.cancel')}
                    </button>
                )}
                <button
                    className="fireknife-payment-methods__btn fireknife-payment-methods__btn--pay fireknife-btn fireknife-btn--primary"
                    type="submit"
                    // cannot do a split if less than 1 usd is one of the parts (either the one paid or the remaining portion)
                    disabled={
                        !stripe ||
                        (amount && maxAmount
                            ? amount < MINIMUM_PAYMENT_AMOUNT ||
                              (maxAmount - amount < MINIMUM_PAYMENT_AMOUNT && amount !== maxAmount)
                            : false)
                    }
                >
                    {t('widgetReservationInfo.payButton')}
                </button>
            </div>
        </form>
    );
}

function StripePaymentBlock({
    amount,
    maxAmount,
    onCancel,
    paymentMethod,
    onStartTransaction,
}: PaymentBlockProps) {
    const { getI18n, t, language } = useLang();
    const stripe = useStripe();
    const elements = useElements();
    const stripeLocale = toStripeLocale(language);
    const appearance = stripeAppearance();
    const [cardToUse, setCardToUse] = useState<string | null>(() => {
        return paymentMethod?.methodInfo?.oldCards[0]?.id || null;
    });
    useEffect(() => {
        setCardToUse(paymentMethod?.methodInfo?.oldCards[0]?.id || null);
    }, [paymentMethod?.methodInfo?.oldCards]);

    const payWithOldCard = useCallback(() => {
        onStartTransaction({
            customerId: paymentMethod?.methodInfo?.customerId,
            oldPaymentMethod: cardToUse,
        });
    }, [cardToUse, onStartTransaction, paymentMethod?.methodInfo?.customerId]);

    if (!stripe || !elements) {
        return null;
    }
    return (
        <div className="fireknife-payment-methods">
            {/* List old card as options and have the last button to register a new card */}
            <h2 className="fireknife-payment-methods__title">
                {getI18n(paymentMethod.name, paymentMethod.nameI18n)}{' '}
                {t('paymentmethods.paymentBlock')}
            </h2>

            {paymentMethod?.methodInfo?.oldCards?.length > 0 && (
                <div className="fireknife-payment-methods__checkables">
                    {paymentMethod.methodInfo.oldCards.map((card: any) => (
                        <Checkable
                            key={card.id}
                            className="fireknife-payment-methods__checkable-box"
                            attributes={{
                                type: 'radio',
                                name: 'oldCard',
                                value: card.id,
                                checked: cardToUse === card.id,
                                onChange: () => setCardToUse(card.id),
                            }}
                            label={t('widgetReservationInfo.card.oldCard', {
                                cardLast4: card.cardLast4,
                                expMonth: card.expMonth,
                                expYear: card.expYear,
                                cardBrand: card.cardBrand,
                            })}
                        />
                    ))}

                    <Checkable
                        className="fireknife-payment-methods__checkable-box"
                        label={t('widgetReservationInfo.card.newCard')}
                        attributes={{
                            type: 'radio',
                            name: 'oldCard',
                            value: 'newCard',
                            onChange: () => {
                                setCardToUse(null);
                            },
                        }}
                    />
                </div>
            )}

            {cardToUse === null && paymentMethod?.methodInfo?.clientSecret && (
                // Stripe Elements caches internal state against the first
                // clientSecret it sees; changing the `options` prop alone
                // doesn't force a re-init. Keying by clientSecret makes
                // Elements remount whenever a fresh SetupIntent is issued —
                // which happens after each successful payment on a split
                // booking — so the next "Register a new card" doesn't try
                // to reuse a spent SetupIntent.
                <Elements
                    // Key on appearance JSON too — Elements caches options,
                    // so a late-arriving per-show theme requires a remount.
                    key={`${paymentMethod.methodInfo.clientSecret}-${JSON.stringify(appearance)}`}
                    options={{
                        clientSecret: paymentMethod.methodInfo.clientSecret,
                        // Stripe 400s on long locale tags (e.g. hu-HU); normalise.
                        locale: stripeLocale,
                        appearance,
                    }}
                    stripe={stripe}
                >
                    <StripeInnerForm
                        onStartTransaction={onStartTransaction}
                        paymentMethod={paymentMethod}
                        methodInfo={paymentMethod?.methodInfo}
                        onCancel={onCancel}
                        amount={amount}
                        maxAmount={maxAmount}
                    />
                </Elements>
            )}

            <div className="fireknife-payment-methods__btn-box">
                {onCancel && cardToUse !== null && (
                    <button
                        className="fireknife-payment-methods__btn fireknife-payment-methods__btn--cancel fireknife-btn fireknife-btn--secondary"
                        onClick={onCancel}
                        type="button"
                    >
                        {t('common.cancel')}
                    </button>
                )}

                {cardToUse && (
                    <button
                        className="fireknife-payment-methods__btn fireknife-payment-methods__btn--pay fireknife-btn fireknife-btn--primary"
                        onClick={payWithOldCard}
                        type="button"
                        // cannot do a split if less than 1 usd is one of the parts (either the one paid or the remaining portion)
                        disabled={
                            amount && maxAmount
                                ? (amount < MINIMUM_PAYMENT_AMOUNT && amount !== maxAmount) ||
                                  (maxAmount - amount < MINIMUM_PAYMENT_AMOUNT &&
                                      amount !== maxAmount)
                                : false
                        }
                    >
                        {t('widgetReservationInfo.payButton')}
                    </button>
                )}
            </div>
        </div>
    );
}

export default StripePaymentBlock;
